/**
 * LedgerExclusionsNote (#568)
 * A quiet, treasurer-facing footnote beneath the all-time ledger that names the
 * bank lines the Plaid bank-pull deliberately — and SILENTLY — leaves out, so a
 * treasurer reconciling against their bank statement reads a missing line as
 * EXPECTED, not "the ledger is wrong." Two by-design exclusions (ADR-072):
 *
 *   1. Untracked accounts — activity on accounts the treasurer didn't select to
 *      track never produces a row. Always applies while a bank is connected.
 *   2. Pre-onboarding history — lines dated on/before the mid-cycle cutoff
 *      (`priorFilingThroughDate`) are history filed OUTSIDE the app, dropped
 *      silently (not even a straggler alert). Shown only for a mid-cycle onboard;
 *      a brand-new committee has no such history to explain.
 *
 * The closed-period straggler case is DELIBERATELY not here — it has its own loud
 * surface (`BoundaryStragglerAlerts`, #565), so this note stays scoped to the two
 * silent drops.
 *
 * Explanation only — nothing here changes what is imported. Self-gates to
 * Finance:Manage (bank linking is a treasurer-grade surface, mirroring
 * `BankConnectionsCard`) and to a LIVE bank-pull (≥1 non-disconnected Plaid
 * connection), so a manual-only committee and a view-only user never see
 * Plaid-specific copy. Copy is centralized at the render boundary in
 * `BankLineExclusionsCopy` (no engineer jargon crosses it).
 */

import { Info } from 'lucide-react';
import { BankConnectionStatus, BankLineExclusionsCopy, preOnboardingHistoryReason } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useBankConnections } from '@/hooks/queries/useBankConnections';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { fmtDateLong } from '../utils';

export function LedgerExclusionsNote() {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  // Bank linking is Manage-tier; skip the Manage-only connections fetch for a
  // view-only viewer rather than firing a doomed 403.
  const { data: connections } = useBankConnections({ enabled: canManage });
  const { data: settings } = useFinanceSettings();

  if (!canManage) return null;

  // These exclusions are Plaid-specific — only relevant once a bank is actively
  // syncing. A disconnected-only (or connection-less) committee gets no note.
  const hasLivePull = (connections ?? []).some(
    (c) => c.status !== BankConnectionStatus.Disconnected,
  );
  if (!hasLivePull) return null;

  // The mid-cycle cutoff is the only pre-onboarding line worth showing — a
  // brand-new committee (null) has no already-filed history to explain.
  const throughDate = settings?.priorFilingThroughDate ?? null;

  return (
    <div
      data-slot="ledger-exclusions-note"
      className="mt-6 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3"
    >
      <div className="flex items-start gap-2.5">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-700">
            {BankLineExclusionsCopy.Heading}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">{BankLineExclusionsCopy.Intro}</p>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
            <li className="flex gap-1.5">
              <span aria-hidden className="text-slate-400">
                •
              </span>
              <span>{BankLineExclusionsCopy.UntrackedAccount}</span>
            </li>
            {throughDate && (
              <li className="flex gap-1.5">
                <span aria-hidden className="text-slate-400">
                  •
                </span>
                <span>{preOnboardingHistoryReason(fmtDateLong(throughDate))}</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default LedgerExclusionsNote;
